import type { CSSProperties } from 'react';

import { IconCalendar } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { FormerMemberBadge } from '@/components/common/FormerMemberBadge';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ProgressSection } from '@/components/ui/ProgressSection';
import { UserAvatar } from '@/components/user/UserAvatar';
import { useLocalizedDate } from '@/hooks/useLocalizedDate';
import { ICON_SIZE_TAB } from '@/constants/ui';
import { STATUS_COLORS, TaskStatus } from '@/types';
import type { Task, TaskListItem } from '@/types';

import styles from './TaskInfoCard.module.css';

type TaskInfoCardData = Pick<
  TaskListItem | Task,
  | 'title'
  | 'status'
  | 'priority'
  | 'assignedToName'
  | 'assignedToAvatarUrl'
  | 'assignedToIsFormerMember'
  | 'dueDate'
  | 'requirementCount'
  | 'completedRequirementCount'
>;

interface TaskInfoCardProps {
  task: TaskInfoCardData;
}

export function TaskInfoCard({ task }: TaskInfoCardProps) {
  const { t } = useTranslation();
  const { formatDateCompact, isOverdue } = useLocalizedDate();

  const totalRequirements = task.requirementCount ?? 0;
  const completedRequirements = task.completedRequirementCount ?? 0;

  const hasAssignee = task.assignedToName != null;
  const hasDueDate = task.dueDate != null;
  const isDueOverdue =
    hasDueDate &&
    isOverdue(task.dueDate) &&
    task.status !== TaskStatus.Completed;

  return (
    <div className={styles.cardBody}>
      <div className={styles.titleRow}>
        <span
          className={styles.statusDot}
          style={{ '--dot-color': STATUS_COLORS[task.status] } as CSSProperties}
          aria-hidden="true"
        />
        <span className={styles.title}>{task.title}</span>
        <StatusBadge type="priority" value={task.priority} size="xs" />
      </div>

      <div className={styles.metaSection}>
        <div className={styles.metaRow}>
          {hasAssignee && (
            <div className={styles.metaGroup}>
              <UserAvatar
                displayName={task.assignedToName ?? ''}
                avatarUrl={task.assignedToAvatarUrl ?? null}
                size="xs"
              />
              <span className={styles.metaText}>{task.assignedToName}</span>
              {task.assignedToIsFormerMember && <FormerMemberBadge />}
            </div>
          )}

          {hasDueDate && (
            <div className={styles.metaGroup}>
              <IconCalendar size={ICON_SIZE_TAB} className={styles.metaIcon} />
              <span
                className={isDueOverdue ? styles.overdueText : styles.metaText}
              >
                {formatDateCompact(task.dueDate)}
              </span>
            </div>
          )}
        </div>

        {totalRequirements > 0 && (
          <ProgressSection
            label={t('fields.requirements')}
            completed={completedRequirements}
            total={totalRequirements}
            size="xs"
          />
        )}
      </div>
    </div>
  );
}
